import { Component, signal, ViewChild, ElementRef, QueryList, ViewChildren, AfterViewChecked } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormGroupDirective } from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../_services/auth.service';
import { EncounterService } from '../_services/encounter.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MfaChallengeType, MfaOptions } from '../_interfaces/mfa';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { NewPasswordComponent } from '../shared/components/password/new-password.component';
import { ResetPassword } from '../shared/forms/reset-passwor-form';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';
import { FirstLoginService } from '../first-login/first-login.service';
import { DevelopmentModeService } from '../_services/development-mode.service';
import { FeatureFlagsService } from '../_services/feature-flags.service';
import { BrandingService } from '../_services/branding.service';

@Component({
  selector: 'app-authentication',
  templateUrl: './authentication.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, TranslateModule, LoadingSpinnerComponent, NewPasswordComponent]
})
export class AuthenticationComponent implements AfterViewChecked {
  @ViewChild('digit1') digit1!: ElementRef<HTMLInputElement>;
  @ViewChild('digit2') digit2!: ElementRef<HTMLInputElement>;
  @ViewChild('digit3') digit3!: ElementRef<HTMLInputElement>;
  @ViewChild('digit4') digit4!: ElementRef<HTMLInputElement>;
  @ViewChild('digit5') digit5!: ElementRef<HTMLInputElement>;
  @ViewChild('digit6') digit6!: ElementRef<HTMLInputElement>;
  @ViewChild('loginForm') authFormDirective?: FormGroupDirective;

  authForm: FormGroup;
  hide = true;
  isLoading = signal<Promise<boolean> | null>(null);
  // MFA state
  mfaChallenge: MfaChallengeType | null = null;
  verificationDigits: string[] = ['', '', '', '', '', ''];
  mfaExhausted: boolean = false;
  verificationError: string | null = null;
  // Recover state
  recoverMode: boolean = false;
  recoverForm: FormGroup | null = null;
  recoverStage: 'email' | 'code' | 'password' = 'email';
  emailError: string | null = null;
  codeError: string | null = null;
  genericError: string | null = null;
  // Password reset state
  expiredPasswordMessage: string | null = null;
  expiredPasswordMode: boolean = false;
  resetPasswordHelper: ResetPassword | null = null;
  recoveryAccessToken: string = '';
  hideConfirmPassword = true;
  // Track if we need to focus on first digit
  private shouldFocusFirstDigit: boolean = false;
  supportUrl = environment.supportUrl;
  versionDisplay = environment.version;
  developmentModeEnabled = false;

  get verificationCode(): string {
    return this.verificationDigits.join('');
  }

  set verificationCode(value: string) {
    const digits = value.split('').slice(0, 6);
    this.verificationDigits = [...digits, ...Array(6 - digits.length).fill('')];
  }
  

  constructor(
    private fb: FormBuilder, 
    private authService: AuthService, 
    private router: Router, 
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private developmentModeService: DevelopmentModeService,
    private flags: FeatureFlagsService,
    public branding: BrandingService,
    private encounterService: EncounterService
  ) {
    this.authForm = this.createAuthForm();
    this.recoverForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      code: ['']
    });
  }

  private createAuthForm(): FormGroup {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  async ngOnInit() {
    this.syncDevelopmentModeFlag();
    this.flags.on('ready', () => this.syncDevelopmentModeFlag());
    this.flags.on('update', () => this.syncDevelopmentModeFlag());

    const temporaryToken = this.route.snapshot.queryParamMap.get('temporaryToken');
    const otpToken = this.route.snapshot.queryParamMap.get('otp');
    
    if (otpToken) {
      try {
        const promise = this.authService.loginWithOtp(otpToken);
        this.isLoading.set(promise);
        if (await promise) await this.navigateAfterLogin();
      } catch (e: any) {
        this.handleHandoffFailure(e);
      } finally {
        this.isLoading.set(null);
      }
      return;
    }
    
    if (temporaryToken) {
      try {
        const promise = this.authService.loginWithTemporaryToken(temporaryToken);
        this.isLoading.set(promise);
        if (await promise) await this.navigateAfterLogin();
      } catch (e: any) {
        this.handleHandoffFailure(e);
      } finally {
        this.isLoading.set(null);
      }
    }
  }

  /**
   * guestGuard lets an SSO handoff onto /login even when a session already exists, so a
   * failed redemption (used OTP after Back, a refresh of /login?otp=, an expired token)
   * would otherwise strand an authenticated user on the login form. Send those sessions
   * into the app instead, and only surface the error when there is nothing to fall back to.
   */
  private handleHandoffFailure(e: any): void {
    if (this.authService.hasUsableSession()) {
      const user = this.authService.getUser();
      const target = user && FirstLoginService.shouldShowFirstTimeLogin(user) ? '/first-login' : '/home';
      this.router.navigate([target]);
      return;
    }

    this.genericError = e?.message
      ? this.translate.instant(e.message)
      : this.translate.instant('An unknown error occurred');
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusFirstDigit && this.mfaChallenge && !this.mfaExhausted && this.digit1) {
      this.digit1.nativeElement.focus();
      this.shouldFocusFirstDigit = false;
    }
  }

  async onSubmit() {
    if (this.authForm.valid) {
      try {
      const { email, password } = this.authForm.value;
      const trimmedPassword = password?.trim() || '';
      const options: MfaOptions | undefined = this.mfaChallenge
        ? {
            mfaMode: this.mfaChallenge === 'verify-email' ? 'MfaEmail' : 'MfaPhone',
            verificationCode: this.verificationCode || undefined,
          }
        : undefined;
      this.verificationError = null;
      const promise = this.authService.login(email, trimmedPassword, options);
      this.isLoading.set(promise);
      if (await promise) await this.navigateAfterLogin();
        this.isLoading.set(null);
      } catch(e: any) {
        // Handle MFA challenge
        if (e.statusCode === 401) {
          const message = (e as any).message as string;
          const lowerMsg = (message || '').toLowerCase();
          if (message === 'No further account recover methods available') {
            this.mfaExhausted = true;
            this.isLoading.set(null);
            return;
          }
          if (this.mfaChallenge && lowerMsg.includes('invalid verification code')) {
            this.verificationError = this.translate.instant('Invalid verification code');
            this.verificationDigits = ['', '', '', '', '', ''];
            this.shouldFocusFirstDigit = true;
            this.isLoading.set(null);
            return;
          }
          if (this.isPasswordExpiredError(message)) {
            this.handleExpiredPassword(e);
            return;
          }
          if (message === 'verify-email' || message === 'verify-sms') {
            if (!this.mfaChallenge) {
              this.mfaChallenge = message as MfaChallengeType;
              this.verificationDigits = ['', '', '', '', '', ''];
              this.mfaExhausted = false;
              this.shouldFocusFirstDigit = true;
            } else if (this.mfaChallenge !== message) {
              // message changed after trying another way
              this.mfaChallenge = message as MfaChallengeType;
              this.verificationDigits = ['', '', '', '', '', ''];
              this.shouldFocusFirstDigit = true;
            } else {
              // same challenge and another-try requested but no other methods
              this.mfaExhausted = true;
            }
            this.verificationError = null;
          } else if (this.mfaChallenge) {
            // Invalid verification code or other MFA-related 401
            this.verificationError = message || this.translate.instant('Invalid verification code');
            this.verificationDigits = ['', '', '', '', '', ''];
            this.shouldFocusFirstDigit = true;
          } else {
            this.genericError = e?.message
              ? this.translate.instant(e.message)
              : this.translate.instant('An unknown error occurred');
          }
          // ensure button returns to ready state
          this.isLoading.set(null);
        } else {
          this.genericError = this.resolveNetworkError(e);
          this.isLoading.set(null);
        }
      }
    }
  }

  async tryAnotherWay() {
    if (!this.mfaChallenge) return;
    const { email, password } = this.authForm.value;
    const trimmedPassword = password?.trim() || '';
    this.verificationDigits = ['', '', '', '', '', ''];
    this.verificationError = null;
    try {
      const nextMode = this.mfaChallenge === 'verify-email' ? 'MfaPhone' : 'MfaEmail';
      const promise = this.authService.login(email, trimmedPassword, { mfaMode: nextMode });
      this.isLoading.set(promise);
      if (await promise) await this.navigateAfterLogin();
    } catch (e) {
      if (e && (e as any).statusCode === 401) {
        const message = (e as any).message as string;
        const lower = (message || '').toLowerCase();
        if (lower.includes('no further') || lower.includes('no more')) {
          this.mfaExhausted = true;
          return;
        }
        if (message === 'verify-sms') {
          this.mfaChallenge = 'verify-sms';
          this.shouldFocusFirstDigit = true;
        } else if (message === 'verify-email') {
          // no more methods available after toggling back
          this.mfaExhausted = true;
        } else {
          // Any other 401 in this flow means no other methods are available
          this.mfaExhausted = true;
        }
      }
    } finally {
      this.isLoading.set(null);
    }
  }

  resetToLogin() {
    // Reset form state including submitted state using FormGroupDirective
    if (this.authFormDirective) {
      this.authFormDirective.resetForm();
    }
    
    this.mfaChallenge = null;
    this.verificationDigits = ['', '', '', '', '', ''];
    this.verificationError = null;
    this.mfaExhausted = false;
    this.genericError = null;
  }

  openRecover(event: Event) {
    event.preventDefault();
    if (this.isLoading() !== null) return;
    this.recoverMode = true;
    this.recoverStage = 'email';
    this.genericError = null;
    this.expiredPasswordMessage = null;
    const codeCtrl = this.recoverForm?.get('code');
    codeCtrl?.clearValidators();
    codeCtrl?.updateValueAndValidity();
    const emailCtrl = this.recoverForm?.get('email');
    emailCtrl?.enable({ emitEvent: false });
    this.emailError = null;
  }

  private isPasswordExpiredError(message: string): boolean {
    const lower = (message || '').toLowerCase();
    return lower.includes('password') && lower.includes('expired');
  }

  private handleExpiredPassword(error: any): void {
    const email = this.authForm.get('email')?.value ?? '';
    const accessToken = error?.accessToken;

    if (accessToken) {
      this.openDirectPasswordReset(accessToken);
    } else if (this.mfaChallenge) {
      this.handleExpiredPasswordAfterMfa(email);
      return;
    } else {
      const translatedMessage = this.translate.instant('Your password is expired');
      this.openRecoverForExpiredPassword(email, translatedMessage);
    }
    this.isLoading.set(null);
  }

  private async openDirectPasswordReset(accessToken: string): Promise<void> {
    this.recoveryAccessToken = accessToken;
    this.recoverMode = true;
    this.expiredPasswordMode = true;
    this.expiredPasswordMessage = null;
    this.genericError = null;

    try {
      const patternData = await this.authService.getPasswordPattern(accessToken);
      this.resetPasswordHelper = new ResetPassword(patternData, false);
      this.recoverStage = 'password';
    } catch {
      this.recoverStage = 'password';
      this.resetPasswordHelper = new ResetPassword({ passwordLength: 8 }, false);
    }
  }

  openRecoverForExpiredPassword(email: string, expiredMessage: string): void {
    this.recoverMode = true;
    this.recoverStage = 'email';
    this.expiredPasswordMessage = expiredMessage;
    this.genericError = null;
    
    const emailCtrl = this.recoverForm?.get('email');
    emailCtrl?.setValue(email, { emitEvent: false });
    emailCtrl?.enable({ emitEvent: false });
    
    const codeCtrl = this.recoverForm?.get('code');
    codeCtrl?.clearValidators();
    codeCtrl?.updateValueAndValidity();
    
    this.emailError = null;
    this.codeError = null;
  
    
    this.snackBar.open(expiredMessage, this.translate.instant('Close'), {
      duration: 5000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom'
    });
  }

  private async handleExpiredPasswordAfterMfa(email: string): Promise<void> {
    const mfaCode = this.verificationCode;

    const initResult = await this.authService.requestPasswordRecovery(email);
    if (initResult.status === 'rate_limited') {
      this.genericError = this.translate.instant('Too many attempts. Please try again later.');
      this.isLoading.set(null);
      return;
    }
    if (initResult.status === 'error') {
      this.genericError = initResult.message || this.translate.instant('Unable to send recovery email. Please try again later.');
      this.isLoading.set(null);
      return;
    }
    if (initResult.status === 'invalid_code') {
      this.genericError = this.translate.instant('Unable to send recovery email. Please try again later.');
      this.isLoading.set(null);
      return;
    }

    const verifyResult = await this.authService.requestPasswordRecovery(email, mfaCode);
    if (verifyResult.status === 'verified') {
      this.mfaChallenge = null;
      await this.openDirectPasswordReset(verifyResult.accessToken);
    } else {
      this.openRecoverForExpiredPasswordAtCodeStage(email);
    }
    this.isLoading.set(null);
  }

  private openRecoverForExpiredPasswordAtCodeStage(email: string): void {
    this.mfaChallenge = null;
    this.recoverMode = true;
    this.recoverStage = 'code';
    this.expiredPasswordMessage = this.translate.instant('Your password is expired');
    this.genericError = null;

    const emailCtrl = this.recoverForm?.get('email');
    emailCtrl?.setValue(email, { emitEvent: false });
    emailCtrl?.disable({ emitEvent: false });

    const codeCtrl = this.recoverForm?.get('code');
    codeCtrl?.setValidators([Validators.required, Validators.minLength(6)]);
    codeCtrl?.updateValueAndValidity();

    this.emailError = null;
    this.codeError = null;
  }

  closeRecover() {
    this.recoverMode = false;
    this.recoverStage = 'email';
    this.recoverForm?.reset();
    const codeCtrl = this.recoverForm?.get('code');
    codeCtrl?.clearValidators();
    codeCtrl?.updateValueAndValidity();
    const emailCtrl = this.recoverForm?.get('email');
    emailCtrl?.enable({ emitEvent: false });
    this.emailError = null;
    this.codeError = null;
    this.resetPasswordHelper = null;
    this.recoveryAccessToken = '';
    this.genericError = null;
    this.expiredPasswordMessage = null;
    this.expiredPasswordMode = false;
    this.mfaChallenge = null;
    this.verificationDigits = ['', '', '', '', '', ''];
    this.verificationError = null;
    this.mfaExhausted = false;
  }

  async onRecover() {
    if (!this.recoverForm) return;
    const email = this.recoverForm.get('email')?.value;
    if (this.recoverStage === 'email') {
      if (!email || this.recoverForm.get('email')?.invalid) return;
      this.emailError = null;
      this.isLoading.set(Promise.resolve(true));
      const result = await this.authService.requestPasswordRecovery(email);
      this.isLoading.set(null);

      if (result.status === 'code_sent' || result.status === 'verified') {
        this.recoverStage = 'code';
        this.recoverForm.get('email')?.disable({ emitEvent: false });
        this.recoverForm.get('code')?.setValidators([Validators.required, Validators.minLength(6)]);
        this.recoverForm.get('code')?.updateValueAndValidity();
      } else if (result.status === 'rate_limited') {
        this.emailError = this.translate.instant('Too many attempts. Please try again later.');
      } else if (result.status === 'error') {
        this.emailError = result.message || this.translate.instant('Unable to send recovery email. Please try again later.');
      } else {
        this.emailError = this.translate.instant('Unable to send recovery email. Please try again later.');
      }
      return;
    }
    if (this.recoverStage === 'code') {
      const code = this.recoverForm.get('code')?.value;
      if (!code || this.recoverForm.get('code')?.invalid) return;
      this.isLoading.set(Promise.resolve(true));
      const result = await this.authService.requestPasswordRecovery(email, code);

      if (result.status === 'verified') {
        this.codeError = null;
        this.genericError = null;
        const codeCtrl = this.recoverForm.get('code');
        codeCtrl?.setErrors(null);
        this.recoveryAccessToken = result.accessToken;
        await this.initializePasswordResetForm();
      } else {
        const codeCtrl = this.recoverForm.get('code');
        if (result.status === 'rate_limited') {
          this.codeError = this.translate.instant('Too many attempts. Please try again later.');
        } else {
          this.codeError = this.translate.instant('Invalid verification code');
        }
        codeCtrl?.setErrors({ ...(codeCtrl?.errors || {}), invalidCode: true });
        codeCtrl?.markAsTouched();
      }
      this.isLoading.set(null);
    }
  }

  private async initializePasswordResetForm(): Promise<void> {
    try {
      const patternData = await this.authService.getPasswordPattern(this.recoveryAccessToken);
      this.resetPasswordHelper = new ResetPassword(patternData, false);
      this.recoverStage = 'password';
    } catch (e: any) {
      const message = e?.error?.message || e?.message || 'Failed to initialise password reset';
      this.snackBar.open(message, 'Close', { duration: 5000 });
    }
  }

  async onPasswordReset() {
    if (!this.resetPasswordHelper || this.resetPasswordHelper.getForm().invalid) return;
    
    const newPassword = this.resetPasswordHelper.getForm().get('newPassword')?.value;
    try {
      this.isLoading.set(Promise.resolve(true));
      await this.authService.changePassword(newPassword, this.recoveryAccessToken);
      this.closeRecover();
    } catch (e: any) {
      const message = e?.error?.message || e?.message || 'Failed to reset password';
      this.genericError = message;
    } finally {
      this.isLoading.set(null);
    }
  }

  async onSsoLogin(sourceApp: string) {
    if (this.isLoading() !== null) return;
    this.authService.redirectToSsoLogin(sourceApp);
  }

  onDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow numeric input
    if (value && !/^\d$/.test(value)) {
      this.verificationDigits[index] = '';
      input.value = '';
      return;
    }

    // Update the digit
    if (value) {
      this.verificationDigits[index] = value;
      // Move to next input if available
      if (index < 5) {
        this.focusDigit(index + 1);
      }
    }
  }

  onDigitKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    
    // Handle Enter key
    if (event.key === 'Enter') {
      event.preventDefault();
      // Submit if verification code is complete and not loading
      if (this.verificationCode.length === 6 && this.isLoading() === null) {
        this.onSubmit();
      }
      return;
    }
    
    // Handle backspace
    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        // Move to previous input if current is empty
        this.verificationDigits[index] = '';
        this.focusDigit(index - 1);
      } else if (input.value) {
        // Clear current input
        this.verificationDigits[index] = '';
      }
    }
    // Handle arrow keys
    else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusDigit(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusDigit(index + 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      this.verificationCode = digits;
      // Focus the next empty digit or the last one
      const nextIndex = Math.min(digits.length, 5);
      setTimeout(() => this.focusDigit(nextIndex), 0);
    }
  }

  private focusDigit(index: number): void {
    const digitRefs = [
      this.digit1,
      this.digit2,
      this.digit3,
      this.digit4,
      this.digit5,
      this.digit6
    ];
    
    if (digitRefs[index]) {
      digitRefs[index].nativeElement.focus();
    }
  }

  onRecoveryCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Filter non-digits and limit to 6 characters
    const filtered = input.value.replace(/\D/g, '').slice(0, 6);
    if (filtered !== input.value) {
      input.value = filtered;
      this.recoverForm?.get('code')?.setValue(filtered, { emitEvent: false });
    }
  }

  private async navigateAfterLogin(): Promise<void> {
    // Always consume launch context here so it cannot leak into a later login
    // in the same tab (first-login, failed auto-start, or a different user).
    const launchPatient = this.authService.consumeLaunchPatient();
    const user = this.authService.getUser();
    if (user && FirstLoginService.shouldShowFirstTimeLogin(user)) {
      this.router.navigate(['/first-login']);
      return;
    }

    // On an Epic/SMART launch-in-context, auto-start a consultation with the patient
    // details supplied by the SSO server (mirrors the Flutter Scribe behaviour).
    if (launchPatient) {
      try {
        await this.encounterService.createAndNavigate(launchPatient, 'consultation');
        return;
      } catch {
        // If auto-start fails, fall back to Home so the user isn't left stranded.
      }
    }

    this.router.navigate(['/home']);
  }

  private isNetworkError(e: any): boolean {
    return e?.status === 0 || e?.statusCode === 0 || e?.error instanceof ProgressEvent;
  }

  private resolveNetworkError(e: any): string {
    if (this.isNetworkError(e)) {
      if (!navigator.onLine) {
        return this.translate.instant('No internet connection. Please check your connection and try again.');
      }
      return this.translate.instant('An error occurred, please try again later.');
    }
    return e?.message ? this.translate.instant(e.message) : this.translate.instant('An unknown error occurred');
  }

  onVersionDoubleClick(): void {
    if (!this.developmentModeEnabled) return;

    const active = !this.developmentModeService.isActive();
    this.developmentModeService.setActive(active);
    this.snackBar.open(
      active ? 'development mode active' : 'development mode desactive',
      this.translate.instant('Close'),
      {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      }
    );
  }

  private syncDevelopmentModeFlag(): void {
    this.developmentModeEnabled = this.flags.isEnabled('developmentMode');
  }
} 